/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { HasLifecycle, User, UserRef } from "../data/types";

export const useUtilities = () => {

    return useMemo(() => {
        const oneKB = 1024;
        const oneMB = oneKB * 1024;
        const oneGB = oneMB * 1024;

        //Formatting
        const formatSize = (size: number) => {
            if (size == 0) return '';
            if (size > oneGB) return `${(size / oneGB).toFixed(1)} GB`;
            else if (size > oneMB) return `${(size / oneMB).toFixed(1)} MB`;
            else if (size > oneKB) return `${(size / oneKB).toFixed(1)} KB`;
            else return `${size} Bytes`;
        }
        const formatCurrency = (amount?: number | string, locale = 'en-US', currency = 'USD') => {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
            }).format(Number(amount));
        }
        const toPascalCase = (txt: string) => {
            return txt[0].toUpperCase() + txt.slice(1);
        }

        //Date Stuff
        const formatDate = (dateTime?: Date, options?: Intl.DateTimeFormatOptions, dateOnly: boolean = false) => {
            if (!dateTime) return '';

            const dateVal = new Date(dateTime);

            const timeOptions: Intl.DateTimeFormatOptions = {
                hour: 'numeric',
                minute: '2-digit',
                //second: '2-digit',                    
                //hour12: false, // Use 24-hour format
            };

            const dateTimeStr = dateOnly ? dateVal.toLocaleDateString('en-US', options) : ` ${dateVal.toLocaleTimeString('en-US', { ...options, ...timeOptions })}`;
            return `${dateTimeStr}`;
        }
        const validDate = (date?: Date) => {
            if (!date) return undefined;
            else if (new Date(date).getTime() <= new Date(0).getTime()) return undefined;
            else return new Date(date);
        }
        const isSameDay = (d1: Date, d2: Date) => {
            d1 = new Date(d1);

            return d1?.getFullYear() === d2?.getFullYear() &&
                d1?.getMonth() === d2?.getMonth() &&
                d1?.getDate() === d2?.getDate();
        }
        const isLastMonth = (theDate: Date) => {
            theDate = new Date(theDate);
            const today = new Date();
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
            
            return theDate >= lastMonth && theDate <= lastMonthEnd;
        }
        const isCurrentMonth = (theDate: Date) => {
            theDate = new Date(theDate);
            const today = new Date();
            
            return theDate.getFullYear() === today.getFullYear() &&
                theDate.getMonth() === today.getMonth();
        }

        const getWeekRange = (date: Date): { startOfWeek: Date; endOfWeek: Date } => {
            const dayOfWeek = date.getDay(); // 0 (Sun) to 6 (Sat)
            const startOfWeek = new Date(date);
            const endOfWeek = new Date(date);

            startOfWeek.setDate(date.getDate() - dayOfWeek); // go back to Sunday
            endOfWeek.setDate(date.getDate() + (6 - dayOfWeek)); // go forward to Saturday

            // Normalize times to start/end of the day if needed
            startOfWeek.setHours(0, 0, 0, 0);
            endOfWeek.setHours(23, 59, 59, 999);

            return { startOfWeek, endOfWeek };
        }
        const compareDates = (date1?: Date, date2?: Date): number => {
            const d1 = new Date(date1 || 0);
            const d2 = new Date(date2 || 0);
            
            if (d1.getTime() < d2.getTime()) return -1;
            if (d1.getTime() > d2.getTime()) return 1;
            return 0;
        }
        const isDateLessThan = (date1?: Date, date2?: Date): boolean => {
            return compareDates(date1, date2) < 0;
        }
        const isDateGreaterThan = (date1?: Date, date2?: Date): boolean => {
            return compareDates(date1, date2) > 0;
        }
        const isDateEqualTo = (date1?: Date, date2?: Date): boolean => {
            return compareDates(date1, date2) == 0;
        }
        const daysBetween = (date1: Date, date2: Date): number => {
            const d1 = new Date(date1);
            const d2 = new Date(date2);
            
            const diffMs = Math.abs(d2.getTime() - d1.getTime());
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            
            return diffDays;
        }
        

        //User Stuff
        const getUsersName = (usernameOrId: string, users: UserRef[] | User[]) => {
            const theUser = users.find((u) => u.username == usernameOrId || u.userId == usernameOrId);
            return theUser?.name || usernameOrId;
        }

        //Hashing
        const getSHA256Hash = async (input: string) => {
            const textAsBuffer = new TextEncoder().encode(input);
            const hashBuffer = await window.crypto.subtle.digest("SHA-256", textAsBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hash = hashArray
                .map((item) => item.toString(16).padStart(2, "0"))
                .join("");
            return hash;
        };

        //HTTP
        const cleanUrl = (url: string) => {
            return url.replace(/^\\*["']+|["']+\\*$/g, '');
        }

        //Lifecycle
        const filterNotDeleted = (item: any): boolean => {
            return !item.lifecycle || !item.lifecycle?.deletedBy;
        }
        const isDeleted = (item: any): boolean => {
            return !!item.lifecycle?.deletedBy;
        }

        return {
            formatSize,
            date: {
                daysBetween,
                formatDate,
                getWeekRange,
                isSameDay,
                isToday: (date: Date) => isSameDay(date, new Date()),
                compareDates,
                isDateLessThan,
                isDateGreaterThan,
                isDateEqualTo,
                isLastMonth,
                isCurrentMonth,
                validDate
            },
            http: {
                cleanUrl
            },
            formatCurrency,
            getSHA256Hash,
            toPascalCase,
            getUsersName,
            lifecycle: {
                filterNotDeleted,
                isDeleted
            }
        };
    }, []);
}